import { Sync as SyncIcon } from "@mui/icons-material";
import { Box, Button, Card, CardContent, Grid, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MarketGrowthAdminApi from "../../../../../infrastructure/api/marketGrowthAdmin";
import LineChartCustom from "../../../../components/shared/chart/LineChartCustom";
import { formatMoney } from "../../../partner-commission/utils/formatMoney";
import {
  PARTNER_COMMISSION_STATUS_LABELS,
} from "../../../partner-commission/constants/portalEnums";
import BreakdownTable from "../components/BreakdownTable";
import MarketGrowthDataTable from "../components/MarketGrowthDataTable";
import MarketGrowthPageShell from "../components/MarketGrowthPageShell";
import MarketGrowthStatCard from "../components/MarketGrowthStatCard";
import { getEnumLabel, TRIGGER_EVENT_LABELS } from "../constants/commissionEnums";
import { PARTNER_STATUS_LABELS } from "../constants/partnerEnums";
import { buildMarketGrowthPath } from "../marketGrowthMenuRouteConfig";
import {
  formatMonthLabel,
  getAttributionStatusLabel,
} from "../constants/reportEnums";

const normalizeBreakdown = (items) =>
  (Array.isArray(items) ? items : []).map((item) => ({
    key: item.key ?? item.Key,
    count: item.count ?? item.Count ?? 0,
    amount: item.amount ?? item.Amount ?? 0,
  }));

const MarketGrowthDashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await MarketGrowthAdminApi.getDashboard();
      const normalized = MarketGrowthAdminApi.normalize(
        res,
        "بارگذاری داشبورد ناموفق بود."
      );
      if (normalized.error) {
        setError(normalized.error);
        setData(null);
        return;
      }
      setData(normalized.data);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری داشبورد");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, location.key]);

  const trendChartData = useMemo(() => {
    const items = data?.monthlyCommissionTrend ?? data?.MonthlyCommissionTrend ?? [];
    return items.map((item) => ({
      x: formatMonthLabel(item.year ?? item.Year, item.month ?? item.Month),
      amount: Number(item.amount ?? item.Amount ?? 0),
    }));
  }, [data]);

  const topPartners = useMemo(() => {
    const items = data?.topPartners ?? data?.TopPartners ?? [];
    return Array.isArray(items) ? items : [];
  }, [data]);

  const trendTableRows = useMemo(
    () =>
      trendChartData.map((row) => ({
        id: row.x,
        month: row.x,
        amountLabel: formatMoney(row.amount),
      })),
    [trendChartData]
  );

  const topPartnerRows = useMemo(
    () =>
      topPartners.map((row) => {
        const partnerId = row.partnerId ?? row.PartnerId;
        return {
          id: partnerId,
          partnerLabel:
            (row.partnerDisplayName ?? row.PartnerDisplayName)
            || (row.referralCode ?? row.ReferralCode)
            || partnerId,
          commissionCount: row.commissionCount ?? row.CommissionCount ?? 0,
          amountLabel: formatMoney(row.commissionAmount ?? row.CommissionAmount),
        };
      }),
    [topPartners]
  );

  const trendHeadCells = useMemo(
    () => [
      { id: "month", label: "ماه" },
      { id: "amountLabel", label: "مبلغ" },
    ],
    []
  );

  const topPartnerHeadCells = useMemo(
    () => [
      { id: "partnerLabel", label: "نماینده" },
      { id: "commissionCount", label: "تعداد", type: "number" },
      { id: "amountLabel", label: "مبلغ" },
    ],
    []
  );

  const pick = (camel, pascal) => data?.[camel] ?? data?.[pascal];

  const pendingApprovalPartners = useMemo(() => {
    const direct = pick("pendingApprovalPartners", "PendingApprovalPartners");
    if (direct != null) return direct;
    const breakdown = pick("partnersByStatus", "PartnersByStatus") ?? [];
    const item = breakdown.find(
      (row) => String(row.key ?? row.Key).toLowerCase() === "pendingactivation"
    );
    return item?.count ?? item?.Count ?? 0;
  }, [data]);

  return (
    <MarketGrowthPageShell
      title="داشبورد توسعه بازار"
      description="نمای کلی ماژول نمایندگی، رهگیری معرف و پورسانت."
      loading={loading}
      error={error}
      onRetry={load}
    >
      <Box display="flex" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="outlined" startIcon={<SyncIcon />} onClick={load}>
          بروزرسانی
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MarketGrowthStatCard label="کل نمایندگان" value={pick("totalPartners", "TotalPartners") ?? 0} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MarketGrowthStatCard label="نمایندگان فعال" value={pick("activePartners", "ActivePartners") ?? 0} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MarketGrowthStatCard
            label="در انتظار تأیید"
            value={pendingApprovalPartners}
            subtitle="مشاهده لیست تأیید"
            highlight={pendingApprovalPartners > 0}
            onClick={() => navigate(buildMarketGrowthPath("PartnerApprovals"))}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MarketGrowthStatCard label="سطح ۱" value={pick("level1Partners", "Level1Partners") ?? 0} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MarketGrowthStatCard label="سطح ۲" value={pick("level2Partners", "Level2Partners") ?? 0} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MarketGrowthStatCard label="معرف عمومی" value={pick("publicReferrers", "PublicReferrers") ?? 0} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MarketGrowthStatCard label="کل رهگیری‌ها" value={pick("totalAttributions", "TotalAttributions") ?? 0} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MarketGrowthStatCard label="ثبت‌نام از معرف" value={pick("registeredAttributions", "RegisteredAttributions") ?? 0} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MarketGrowthStatCard label="پروژه از معرف" value={pick("projectCreatedAttributions", "ProjectCreatedAttributions") ?? 0} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MarketGrowthStatCard label="تبدیل‌شده" value={pick("convertedAttributions", "ConvertedAttributions") ?? 0} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MarketGrowthStatCard label="کل پورسانت" value={formatMoney(pick("totalCommissions", "TotalCommissions"))} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MarketGrowthStatCard label="پورسانت در انتظار" value={formatMoney(pick("pendingCommissionsAmount", "PendingCommissionsAmount"))} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MarketGrowthStatCard label="پورسانت تأییدشده" value={formatMoney(pick("approvedCommissionsAmount", "ApprovedCommissionsAmount"))} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MarketGrowthStatCard label="قابل تسویه" value={formatMoney(pick("payableCommissionsAmount", "PayableCommissionsAmount"))} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MarketGrowthStatCard label="پرداخت‌شده" value={formatMoney(pick("paidCommissionsAmount", "PaidCommissionsAmount"))} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MarketGrowthStatCard label="درخواست تسویه" value={formatMoney(pick("totalPayoutRequestedAmount", "TotalPayoutRequestedAmount"))} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MarketGrowthStatCard label="تسویه پرداخت‌شده" value={formatMoney(pick("totalPayoutPaidAmount", "TotalPayoutPaidAmount"))} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MarketGrowthStatCard label="پورسانت ماه جاری" value={formatMoney(pick("currentMonthCommissionsAmount", "CurrentMonthCommissionsAmount"))} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MarketGrowthStatCard label="پروژه ماه جاری" value={pick("currentMonthProjectsCount", "CurrentMonthProjectsCount") ?? 0} />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <BreakdownTable
            title="نمایندگان بر اساس وضعیت"
            rows={normalizeBreakdown(pick("partnersByStatus", "PartnersByStatus"))}
            formatLabel={(key) => getEnumLabel(PARTNER_STATUS_LABELS, key)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <BreakdownTable
            title="رهگیری‌ها بر اساس وضعیت"
            rows={normalizeBreakdown(pick("attributionsByStatus", "AttributionsByStatus"))}
            formatLabel={(key) => getAttributionStatusLabel(key)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <BreakdownTable
            title="پورسانت بر اساس وضعیت"
            rows={normalizeBreakdown(pick("commissionsByStatus", "CommissionsByStatus"))}
            showAmount
            formatLabel={(key) => getEnumLabel(PARTNER_COMMISSION_STATUS_LABELS, key)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <BreakdownTable
            title="پورسانت بر اساس رویداد"
            rows={normalizeBreakdown(pick("commissionsByTriggerEvent", "CommissionsByTriggerEvent"))}
            showAmount
            formatLabel={(key) => getEnumLabel(TRIGGER_EVENT_LABELS, key)}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={7}>
          <Card variant="outlined">
            <CardContent>
              <LineChartCustom
                title="روند پورسانت ۶ ماه اخیر"
                xAxis="x"
                data={trendChartData}
                series={["amount"]}
                yAxisTitle="مبلغ (ریال)"
                fill
              />
              {trendChartData.length === 0 ? null : (
                <Box sx={{ mt: 2 }}>
                  <MarketGrowthDataTable
                    rowKey="id"
                    rows={trendTableRows}
                    headCells={trendHeadCells}
                    showFilter={false}
                    showIndex={false}
                    showPaging={false}
                    tableMinWidth="auto"
                    isDashboard
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                برترین نمایندگان
              </Typography>
              {topPartners.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  داده‌ای وجود ندارد.
                </Typography>
              ) : (
                <MarketGrowthDataTable
                  rowKey="id"
                  rows={topPartnerRows}
                  headCells={topPartnerHeadCells}
                  showFilter={false}
                  showIndex={false}
                  showPaging={false}
                  tableMinWidth="auto"
                  isDashboard
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </MarketGrowthPageShell>
  );
};

export default MarketGrowthDashboardPage;
